import React from "react";
import { Link } from "react-router-dom";
import { useT } from "../i18n/LanguageContext";
import { Linkedin, Facebook, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";

// Clean X (Twitter) glyph — lucide's `Twitter` is still the old bird.
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.91l-4.41-5.52L6.6 22H3.34l8.04-9.19L2 2h7.06l3.98 5.07L18.244 2zm-1.21 18h1.87L7.04 4H5.06l11.97 16z" />
  </svg>
);

const OFFICES = [
  {
    country: "DR Congo",
    city: "Kinshasa",
    address: "Av. Col Mondjiba 2366, C/Gombe",
    phone: "+243 81 558 6725",
  },
];

export default function Footer() {
  const { t } = useT();
  const year = new Date().getFullYear();

  return (
    <footer className="relative border-t border-white/10 bg-brand-bg" data-testid="site-footer">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
        {/* CTA */}
        <div className="grid items-end gap-10 border-b border-white/10 py-16 md:grid-cols-2 md:py-20">
          <div>
            <p className="font-mono-tag">07 · Get in touch</p>
            <h3 className="mt-4 font-heading text-3xl tracking-tight text-white sm:text-4xl lg:text-5xl">
              {t("cta.footer.title")}
            </h3>
            <p className="mt-4 max-w-lg text-base text-brand-textDim">{t("cta.footer.sub")}</p>
          </div>
          <div className="flex md:justify-end">
            <Link
              to="/contact"
              data-testid="footer-cta"
              className="group inline-flex items-center gap-2 bg-brand-red px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-brand-redHover"
            >
              {t("cta.footer.btn")}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>

        {/* Offices grid */}
        <div className="grid gap-10 py-16 md:grid-cols-4">
          <div className="md:col-span-2">
            <p className="font-mono-tag">{t("contact.offices_title")}</p>
            <div className="mt-6 grid gap-8 sm:grid-cols-2">
              {OFFICES.map((o) => (
                <div key={o.country} className="space-y-2 text-sm">
                  <div className="font-heading text-lg text-white">{o.country}</div>
                  <div className="flex items-start gap-2 text-brand-textDim">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{o.address}, {o.city}</span>
                  </div>
                  <div className="flex items-center gap-2 text-brand-textDim">
                    <Phone className="h-4 w-4" />
                    <span>{o.phone}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono-tag">{t("contact.email_title")}</p>
            <a
              href="mailto:info@bigradapgroup.com"
              className="mt-6 flex items-center gap-2 text-base text-white transition-colors hover:text-brand-red"
              data-testid="footer-email"
            >
              <Mail className="h-4 w-4" />
              info@bigradapgroup.com
            </a>
          </div>

          <div>
            <p className="font-mono-tag">Follow</p>
            <div className="mt-6 flex gap-3">
              {[
                { Icon: Linkedin, href: "https://www.linkedin.com/company/bigradap-group-technologies-pty-ltd/", label: "LinkedIn", t: "footer-linkedin" },
                { Icon: XIcon, href: "https://x.com/BigradapGroup", label: "X (Twitter)", t: "footer-twitter" },
                { Icon: Facebook, href: "https://www.facebook.com/BigradapGroup/", label: "Facebook", t: "footer-facebook" },
              ].map(({ Icon, href, label, t: tid }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={tid}
                  className="flex h-10 w-10 items-center justify-center border border-white/15 text-white transition-colors hover:border-brand-red hover:text-brand-red"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Huge wordmark */}
        <div className="overflow-hidden border-t border-white/10 py-10">
          <div className="select-none whitespace-nowrap font-heading text-[18vw] font-medium leading-none tracking-tighter text-white/[0.04]">
            BIGRADAP
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-3 border-t border-white/10 py-6 text-xs text-brand-textDim md:flex-row md:items-center">
          <span>© {year} Bigradap Group Technologies (PTY) LTD. {t("footer.rights")}</span>
          <div className="flex items-center gap-4">
            <Link to="/legal" className="hover:text-white" data-testid="footer-legal">
              {t("footer.legal")}
            </Link>
            <span className="font-mono tracking-[0.2em]">{t("footer.tagline")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
